import { useLocation } from "wouter";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ListChecks } from "lucide-react";
import { ChecklistDailyTab } from "./checklist-daily-tab";
import { ChecklistEventTab } from "./checklist-event-tab";

// Phase 49 Plan 08 (operator, 2026-07-14, verbatim): "Under Task sidebar
// menu, it shows Checklist, and under Checklist it will reflect multiple
// tabs: 1. Daily, Weekly, Monthly, Onboarding, Exit, etc." This is the
// tabbed container — Daily/Weekly/Monthly reuse the period read path
// (checklist-daily-tab.tsx), Onboarding/Exit consume the new N-per-template
// event feed (checklist-event-tab.tsx). Route/tab-sync mirrors the
// established inventory-operation.tsx convention: literal path segments
// registered before the catch-all /tasks/:id (App.tsx), tab derived from
// the URL so it's bookmarkable/shareable.

type TabValue = "daily" | "weekly" | "monthly" | "onboarding" | "exit";

const TAB_VALUES: readonly TabValue[] = ["daily", "weekly", "monthly", "onboarding", "exit"];

function tabFromPath(path: string): TabValue {
  // Match /tasks/checklist/<tab>; fall back to "daily" on bare /tasks/checklist.
  const suffix = path.replace(/^\/tasks\/checklist\/?/, "").split("/")[0] ?? "";
  return (TAB_VALUES as readonly string[]).includes(suffix) ? (suffix as TabValue) : "daily";
}

export default function ChecklistPage() {
  const [location, setLocation] = useLocation();
  const tab = tabFromPath(location);

  function handleTabChange(next: string) {
    if (next === tab) return;
    setLocation(`/tasks/checklist/${next}`);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <ListChecks className="h-6 w-6 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Checklist</h1>
          <p className="text-sm text-muted-foreground">
            Daily, weekly, and monthly rounds, plus onboarding &amp; exit procedures spawned by
            bookings and lifecycle events.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList className="flex w-full overflow-x-auto sm:grid sm:grid-cols-5 h-auto sm:h-10">
          <TabsTrigger value="daily" className="shrink-0 whitespace-nowrap">Daily</TabsTrigger>
          <TabsTrigger value="weekly" className="shrink-0 whitespace-nowrap">Weekly</TabsTrigger>
          <TabsTrigger value="monthly" className="shrink-0 whitespace-nowrap">Monthly</TabsTrigger>
          <TabsTrigger value="onboarding" className="shrink-0 whitespace-nowrap">Onboarding</TabsTrigger>
          <TabsTrigger value="exit" className="shrink-0 whitespace-nowrap">Exit</TabsTrigger>
        </TabsList>

        <TabsContent value="daily"><ChecklistDailyTab trigger="daily" /></TabsContent>
        <TabsContent value="weekly"><ChecklistDailyTab trigger="weekly" /></TabsContent>
        <TabsContent value="monthly"><ChecklistDailyTab trigger="monthly" /></TabsContent>
        <TabsContent value="onboarding"><ChecklistEventTab trigger="onboarding" /></TabsContent>
        <TabsContent value="exit"><ChecklistEventTab trigger="exit" /></TabsContent>
      </Tabs>
    </div>
  );
}
